import React, { useEffect, useState } from 'react';
import { noop } from 'lodash';

import { ClaimContextProvider } from '~/components/ClaimContainer';
import type {
  CommunicationSuggestionClaimNotificationModel,
  UpdateNotification,
} from '~/components/ClaimNotificationsCard/types';
import SendEmailCommunicationCardContainer from '~/components/communications/EmailCommunicationCard/SendEmailCommunicationCardContainer';
import { contactPopulate } from '~/components/Contacts/ContactUtils';
import type { MinimizedComponentBaseProps } from '~/components/core/MinimizedBar/types';
import { ESIGN_SIGNATURE_URL_PLACEHOLDER } from '~/components/Documents/ESign/utils';
import GenericNotificationHeader from '~/components/Notifications/GenericNotificationHeader';
import { serverDateToLocal } from '~/DateTimeUtils';
import { useFetchClaim } from '~/hooks/useFetchClaim';
import { reportErrorInProductionOrThrow } from '~/Utils';

export interface SuggestedEmailNotificationDialogProps extends MinimizedComponentBaseProps {
  claimNotification: CommunicationSuggestionClaimNotificationModel;
  onUpdate: () => void;
  onClose: () => void;
  onMarkAsUnread?: (notificationId: number) => void;
  onUpdateNotification?: UpdateNotification;
}

/**
 * The suggested-email editor, as the minimized-dialog registry instantiates it. The registry renders
 * this outside the notification card's subtree so that minimizing survives navigating away, which is
 * why it fetches its own claim and owns the notification header's optimistic state rather than
 * receiving either from the launcher.
 */
const SuggestedEmailNotificationDialog: React.FC<SuggestedEmailNotificationDialogProps> = ({
  claimNotification,
  onUpdate,
  onClose,
  onMarkAsUnread,
  onUpdateNotification,
  open,
  onMinimized,
  disableMinimized,
}) => {
  const [claim, isLoadingClaim, isError, reloadClaim] = useFetchClaim(claimNotification.claim_id);
  const [optimisticNotification, setOptimisticNotification] =
    useState<CommunicationSuggestionClaimNotificationModel>(claimNotification);

  // `claim_id` is optional on the notification, so useFetchClaim can settle with no claim and no
  // error as readily as it can fail a request; both land here.
  const hasNoClaim = !isLoadingClaim && (!claim || isError);

  useEffect(() => {
    if (!hasNoClaim) {
      return;
    }
    // The registry renders every minimized dialog as a sibling of this one under a single provider
    // with no error boundary in between (MinimizedBar/Context), so throwing here would unmount them
    // all and take their unsent drafts with them. Retire this entry and report instead: `onClose` is
    // the registry's own `handleCloseDialog`, and the launcher already told the notification card to
    // drop the slot it used to render, so an entry left in `dialogContainersRef` renders nothing and
    // nothing can ever retire it -- every retry would add another. Retiring runs before the report
    // because outside production the reporter still throws, which keeps a missing claim loud where no
    // draft is at risk but must not leave the orphan behind.
    onClose();
    reportErrorInProductionOrThrow('No claim for claim notification suggestion');
  }, [hasNoClaim, onClose]);

  if (isLoadingClaim) {
    return null;
  }

  if (!claim || isError) {
    // Reported and retired by the effect above; render nothing while the entry goes away.
    return null;
  }

  const contact = claim.contacts?.find((c) => c.id === claimNotification.to_contact_id);
  const populatedContact = contact ? contactPopulate(contact) : undefined;

  const shouldPassGenericTemplateId =
    claimNotification.generic_template_id && claimNotification.body?.includes(ESIGN_SIGNATURE_URL_PLACEHOLDER);

  return (
    <ClaimContextProvider
      claim={claim}
      refreshData={async () => await reloadClaim()}
      // The registry already sits inside a MinimizedDialogContextProvider; a nested one here would
      // scope any dialog this editor opens to a provider that unmounts with the editor.
      createMinimizedContext={false}
    >
      <SendEmailCommunicationCardContainer
        contact={populatedContact}
        onClose={onClose}
        emailTitle={claimNotification.subject}
        emailText={claimNotification.body}
        onSendEmail={() => {
          onUpdate();
          onClose();
        }}
        communicationHeaderAdditionalProps={{ isDynamicContact: true }}
        exposureIds={[claimNotification.exposure_id ?? 0]}
        communicationSuggestionNotificationId={claimNotification.id}
        AdditionalDialogHeader={
          <GenericNotificationHeader
            notification={optimisticNotification}
            onClose={onClose}
            onMarkAsUnread={onMarkAsUnread ?? noop}
            onUpdateNotification={(claimNotificationId, updates) => {
              setOptimisticNotification((previous) => ({
                ...previous,
                ...updates,
                // The dismiss button reports a boolean; the notification carries the dismissal
                // timestamp, so translate it the way the notifications screen's own merge does.
                datetime_dismissed:
                  updates.datetime_dismissed === undefined
                    ? previous.datetime_dismissed
                    : updates.datetime_dismissed
                      ? serverDateToLocal(new Date())
                      : undefined,
              }));
              onUpdateNotification?.(claimNotificationId, updates);
            }}
          />
        }
        documentTemplateId={
          shouldPassGenericTemplateId ? undefined : (claimNotification.document_template_id ?? undefined)
        }
        genericTemplateId={shouldPassGenericTemplateId ? claimNotification.generic_template_id : undefined}
        open={open}
        onMinimized={onMinimized}
        disableMinimized={disableMinimized}
      />
    </ClaimContextProvider>
  );
};

export default SuggestedEmailNotificationDialog;
